/**
 * LOCKER-534 — display helpers for the temperature demo. Locale-aware so FR
 * renders `4,6 °C` / `jeu. 28 · 14:00` and EN renders `4.6 °C` / `Thu 28 · 14:00`.
 */
import type { TemperatureLevel } from "./temperatureDemoGenerator"

const numberFormats = new Map<string, Intl.NumberFormat>()

/** One decimal, locale separator: `4,6` (fr) / `4.6` (en). */
export const formatTemperatureValue = (v: number, lang: string): string => {
  let f = numberFormats.get(lang)
  if (!f) {
    f = new Intl.NumberFormat(lang, {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    })
    numberFormats.set(lang, f)
  }
  return f.format(v)
}

export const formatTemperature = (v: number, lang: string): string =>
  `${formatTemperatureValue(v, lang)} °C`

const pad = (n: number) => (n < 10 ? `0${n}` : `${n}`)

/** `HH:mm`, or `<weekday> <day> · HH:mm` when `withDay`. */
export const formatClock = (
  now: Date,
  minutesAgo: number,
  lang: string,
  withDay = false,
): string => {
  const d = new Date(now.getTime() - minutesAgo * 60_000)
  const hm = `${pad(d.getHours())}:${pad(d.getMinutes())}`
  if (!withDay) return hm
  const weekday = new Intl.DateTimeFormat(lang, { weekday: "short" }).format(d)
  return `${weekday} ${d.getDate()} · ${hm}`
}

/** `45 min` / `2 h 05`. */
export const formatDuration = (minutes: number): string =>
  minutes < 60
    ? `${minutes} min`
    : `${Math.floor(minutes / 60)} h ${pad(minutes % 60)}`

/** Look of each level — mockup palette. */
export const LEVEL_COLORS: Record<
  TemperatureLevel,
  { text: string; pillBg: string; pillFg: string; pillBorder: string; chipBg: string; chipFg: string }
> = {
  ok: {
    text: "#2E7D32",
    pillBg: "#ffffff",
    pillFg: "#234285",
    pillBorder: "1px dashed rgba(35,66,133,.6)",
    chipBg: "rgba(67,160,71,.1)",
    chipFg: "#1B5E20",
  },
  warn: {
    text: "#8A4200",
    pillBg: "#FFF4E5",
    pillFg: "#8A4200",
    pillBorder: "1px dashed #D97A00",
    chipBg: "#FFF4E5",
    chipFg: "#8A4200",
  },
  crit: {
    text: "#B3261E",
    pillBg: "#B3261E",
    pillFg: "#ffffff",
    pillBorder: "1px dashed rgba(255,255,255,.75)",
    chipBg: "#B3261E",
    chipFg: "#ffffff",
  },
}

/** i18n key of each level's long label. */
export const LEVEL_LABEL_KEYS: Record<TemperatureLevel, string> = {
  ok: "temperatureDemo.levelOk",
  warn: "temperatureDemo.levelWarn",
  crit: "temperatureDemo.levelCrit",
}

export const WARN_COLOR = "#D97A00"
export const CRIT_COLOR = "#B3261E"
